"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type TestimonialSlide = {
  quote: string;
  author: string;
  role: string;
  avatar: { src: string; width: number; height: number };
};

/**
 * The carousel half of Testimonials `slider` (docs/reset-porting.md — the
 * reference's autoplaying testimonial band). A client component ON PURPOSE:
 * autoplay, arrows and dots ARE this layout's anatomy, unlike Faq where the
 * native <details> element made client JS avoidable. The block itself stays
 * server-rendered and hands over plain serialised slides.
 *
 * Autoplay every 4s (the reference's own data-delay), suspended while hovered
 * and skipped entirely under prefers-reduced-motion; arrows and dots always
 * work, and manual navigation restarts the timer so the next auto-advance
 * doesn't cut a freshly chosen slide short.
 */
export function TestimonialSliderTrack({ items }: { items: TestimonialSlide[] }) {
  const [index, setIndex] = useState(0);
  // Timer epoch: bumping it re-runs the interval effect, restarting the 4s.
  const [epoch, setEpoch] = useState(0);
  const hovered = useRef(false);
  const count = items.length;

  useEffect(() => {
    if (count < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => {
      if (!hovered.current) setIndex((i) => (i + 1) % count);
    }, 4000);
    return () => clearInterval(t);
  }, [count, epoch]);

  const goTo = (i: number) => {
    setIndex(((i % count) + count) % count);
    setEpoch((e) => e + 1);
  };

  if (count === 0) return null;

  return (
    <div
      onMouseEnter={() => (hovered.current = true)}
      onMouseLeave={() => (hovered.current = false)}
    >
      <div className="overflow-hidden" role="region" aria-label={`1 / ${count}`}>
        <div
          className="flex transition-transform duration-500 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {items.map((item, i) => (
            <figure key={i} className="w-full shrink-0" aria-hidden={i !== index}>
              <h3 className="!text-xl !font-extrabold !tracking-tight !text-foreground md:!text-2xl">
                {item.author}
              </h3>
              <div className="mt-6 grid items-start gap-y-8 gap-x-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                {item.avatar?.src ? (
                  <div className="overflow-hidden rounded-card">
                    <Image
                      src={item.avatar.src}
                      alt={item.author}
                      width={item.avatar.width}
                      height={item.avatar.height}
                      sizes="(min-width: 1024px) 24rem, 90vw"
                      className="h-auto w-full object-cover"
                    />
                  </div>
                ) : null}
                <div>
                  {item.role ? (
                    <p className="font-display text-[13px] font-medium tracking-[0.11em] uppercase text-muted-foreground">
                      {item.role}
                    </p>
                  ) : null}
                  <blockquote className="mt-4 text-base italic leading-loose text-foreground/85">
                    {item.quote}
                  </blockquote>
                </div>
              </div>
            </figure>
          ))}
        </div>
      </div>

      {count > 1 ? (
        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            type="button"
            aria-label="Previous"
            onClick={() => goTo(index - 1)}
            className="grid size-10 place-items-center text-foreground transition-opacity hover:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="size-6" aria-hidden />
          </button>
          <div className="flex items-center gap-2.5">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Slide ${i + 1}`}
                aria-pressed={i === index}
                onClick={() => goTo(i)}
                className={`size-2.5 rounded-full transition-colors ${
                  i === index ? "bg-foreground" : "bg-foreground/25 hover:bg-foreground/50"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="Next"
            onClick={() => goTo(index + 1)}
            className="grid size-10 place-items-center text-foreground transition-opacity hover:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className="size-6" aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}
